import React, { useEffect, useState, Fragment, useContext } from "react";
import axios from "axios";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import RequireAuthPopup from "./RequireAuthPopup";
import AuthContext from "../contexts/AuthContext";

export const JobPostings = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [authPopupOpen, setAuthPopupOpen] = useState(false);

  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/works`,
        );

        const filteredJobs = response.data
          .filter((job) => job.status === "open")
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 40);
        setJobs(filteredJobs);
      } catch (err) {
        setError("Failed to fetch jobs");
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const handleJobClick = (job) => {
    if (!user) {
      setAuthPopupOpen(true); // Trigger authentication popup if the user is not logged in
    } else {
      setSelectedJob(job);
      setIsOpen(true); // Open job details dialog
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <>
      <RequireAuthPopup
        isOpen={authPopupOpen}
        onClose={() => setAuthPopupOpen(false)}
      />

      <div className="max-w-7xl mx-auto bg-white shadow sm:rounded-lg mt-6">
        <div className="divide-y divide-gray-200">
          {jobs.map((job) => (
            <div
              key={job._id}
              className="px-4 py-4 sm:px-6 flex justify-between items-center cursor-pointer hover:bg-gray-50"
              onClick={() => handleJobClick(job)}
            >
              <div>
                <p className="text-sm font-medium text-indigo-600">
                  {job.title}
                </p>
                <p className="text-sm text-gray-500">{job.shortDescription}</p>
                <p className="text-xs text-gray-400">
                  Posted: {new Date(job.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-green-600">
                  CAD {job.budget.toLocaleString()}
                </p>
                <p className="text-sm text-gray-400">{job.location}</p>
                <p className="text-xs text-gray-400">
                  Deadline: {new Date(job.deadline).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  {selectedJob && (
                    <>
                      <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          onClick={() => setIsOpen(false)}
                        >
                          <span className="sr-only">Close</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>

                      <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                          <Dialog.Title
                            as="h3"
                            className="text-lg font-semibold leading-6 text-gray-900 border-b pb-2"
                          >
                            {selectedJob.title}
                          </Dialog.Title>

                          <div className="mt-4 space-y-4">
                            <div>
                              <h4 className="text-sm font-medium text-gray-500">
                                Description
                              </h4>
                              <p className="mt-1 text-sm text-gray-900">
                                {selectedJob.longDescription}
                              </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-medium text-gray-500">
                                  Budget
                                </h4>
                                <p className="mt-1 text-sm text-gray-900">
                                  ${selectedJob.budget.toLocaleString()}
                                </p>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-500">
                                  Location
                                </h4>
                                <p className="mt-1 text-sm text-gray-900">
                                  {selectedJob.location}
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-medium text-gray-500">
                                  Posted Date
                                </h4>
                                <p className="mt-1 text-sm text-gray-900">
                                  {new Date(
                                    selectedJob.createdAt,
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-500">
                                  Deadline
                                </h4>
                                <p className="mt-1 text-sm text-gray-900">
                                  {new Date(
                                    selectedJob.deadline,
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            {selectedJob.tags &&
                              selectedJob.tags.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-500">
                                    Tags
                                  </h4>
                                  <div className="mt-1 flex flex-wrap gap-2 justify-center sm:justify-start">
                                    {selectedJob.tags.map((tag, index) => (
                                      <span
                                        key={index}
                                        className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                            <div className="mt-6">
                              <h4 className="text-sm font-medium text-gray-500">
                                Contact Email
                              </h4>
                              <p className="mt-1 text-sm text-indigo-600">
                                <a
                                  href="mailto:connect@buildingweve.com"
                                  className="hover:underline"
                                >
                                  connect@buildingweve.com
                                </a>
                              </p>
                            </div>

                            {/* Add Email Section */}
                            {/* {selectedJob.email && (
                              <div className="mt-6">
                                <h4 className="text-sm font-medium text-gray-500">
                                  Contact
                                </h4>
                                <p className="mt-1 text-sm text-indigo-600">
                                  <a
                                    href={`mailto:${selectedJob.creatorEmail}`}
                                    className="hover:underline"
                                  >
                                    {selectedJob.creatorEmail}
                                  </a>
                                </p>
                              </div>
                            )} */}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
